import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Que faire immédiatement quand une vitre se casse à Lyon ?",
    a: "Sécurisez d'abord la zone : éloignez les enfants et les animaux, portez des chaussures fermées. Si possible, protégez l'ouverture avec un carton ou un plastique épais scotché sur le cadre. Ne touchez pas les morceaux de verre tranchants. Appelez-nous ensuite au 09 80 80 36 84 pour une intervention rapide.",
  },
  {
    q: "Pouvez-vous intervenir le jour même pour une vitre cassée ?",
    a: "Nous faisons notre maximum pour intervenir le jour même selon nos disponibilités. Dans tous les cas, nous garantissons une intervention sous 24 à 48 heures. Nous intervenons 7j/7 sur Lyon et la Métropole de Lyon.",
  },
  {
    q: "Remplacez-vous une vitre sur tout type de menuiserie ?",
    a: "Oui, nous intervenons sur tous les types de menuiseries : fenêtres PVC, bois ou aluminium, portes-fenêtres, baies vitrées coulissantes, vérandas, portes d'entrée vitrées. Nous nous adaptons à chaque bâti.",
  },
];

export default function RemplacementVitreCassee() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Remplacement de vitre cassée à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Remplacement de vitre cassée à Lyon : fenêtre, baie, porte. Intervention rapide 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Remplacement vitre cassée', item: 'https://depanfenetres-lyon.fr/remplacement-vitre-cassee-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Remplacement de vitre cassée à Lyon | DFPRO"
        description="Vitre brisée à Lyon ? Remplacement rapide fenêtre, baie ou porte. Vitrier urgence 24-48h, 7j/7. Devis gratuit, garantie 10 ans. Appelez DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/remplacement-vitre-cassee-lyon/"
        ogTitle="Remplacement de vitre cassée à Lyon — Intervention rapide | DFPRO"
        ogDescription="Vitre cassée, fêlée ou brisée ? Vitrier Lyon 24-48h, 7j/7. Devis gratuit, garantie 10 ans."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Remplacement vitre cassée</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/vitre-cassee-hero-2026.avif"
              alt="Remplacement de vitre cassée à Lyon — DFPRO Lyon intervient pour sécuriser et remplacer votre vitrage brisé"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Remplacement de vitre cassée à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Vitre brisée ou fêlée ? Nos vitriers sécurisent votre logement et remplacent votre vitrage cassé. Intervention en urgence à Lyon et dans toute la métropole, 7j/7.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Une vitre cassée ? Voici comment nous intervenons à Lyon
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Une vitre peut se casser pour de multiples raisons : un choc accidentel (meuble, outil de jardin), une tentative d'effraction, un impact de projectile, ou encore un choc thermique quand le verre subit une différence brutale de température. Quelle qu'en soit la cause, un vitrage brisé est à la fois un <strong className="text-foreground-800">danger pour les occupants</strong> (risque de coupure) et une brèche dans l'isolation et la sécurité de votre logement.
                  </p>
                  <p>
                    Dès votre appel au 09 80 80 36 84, nous évaluons l'urgence de la situation. Si la vitre cassée présente un risque immédiat pour votre sécurité, nous faisons le maximum pour <strong className="text-foreground-800">intervenir le jour même</strong> à Lyon et dans les communes voisines. Dans tous les cas, notre délai standard est de 24 à 48 heures. Nous intervenons 7 jours sur 7, y compris le week-end.
                  </p>
                  <p>
                    Sur place, notre vitrier commence par sécuriser la zone : retrait des éclats de verre, protection de l'ouverture si le remplacement ne peut être fait immédiatement. Ensuite, il prend les <strong className="text-foreground-800">mesures précises du vitrage</strong> à remplacer — hauteur, largeur, épaisseur — pour commander ou tailler un verre aux dimensions exactes. Le type de verre choisi dépend de l'usage : verre standard pour une cloison intérieure, double vitrage isolant pour une fenêtre donnant sur l'extérieur, ou <strong className="text-foreground-800">verre feuilleté de sécurité</strong> pour une porte ou une baie exposée.
                  </p>
                  <p>
                    La pose du nouveau vitrage est réalisée avec soin : retrait des anciens joints, nettoyage de la feuillure, mise en place du vitrage avec cales de positionnement, jointoiement au mastic silicone ou remise en place des parcloses. Le résultat est un vitrage parfaitement étanche et solidement fixé.
                  </p>
                  <p>
                    Nous intervenons sur tous les types de menuiseries à Lyon et en métropole : <strong className="text-foreground-800">fenêtres PVC, bois ou aluminium, portes-fenêtres, baies vitrées coulissantes, vérandas, impostes, portes d'entrée</strong>. Chaque remplacement bénéficie de notre garantie 10 ans, pièces et main-d'œuvre.
                  </p>
                  <p className="bg-background-100 p-4 rounded-md text-foreground-700">
                    <strong>Conseil :</strong> en attendant notre intervention, ne retirez pas vous-même les morceaux de verre. Protégez simplement l'ouverture avec un carton épais ou un film plastique, fixé au cadre (pas au verre). Portez des gants si vous devez manipuler quoi que ce soit à proximité.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Vitrage embué', href: '/double-vitrage-embue-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                    { label: 'Vitrier urgence', href: '/vitrier-urgence-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Une vitre cassée à Lyon ?"
          subtitle="Ne restez pas avec une ouverture béante. Appelez le 09 80 80 36 84, intervention rapide 7j/7."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}